import React, { Component } from 'react';
import {
  Popover, Icon, Tree, Row, Col, Checkbox,
} from 'antd';
import { PopoverButton, FilterPanelButton } from './styles';

export const generateFilterTreeObj = filters => Object.keys(filters).map(item => (
  {
    id: `0-|${item}`,
    label: <b>{item}</b>,
    innerList: Object.keys(filters[item]).map(innerItem => (
      { id: `0-|${item}-|${innerItem}`, label: innerItem }
    )),
  }
));

export const mapGlobalFilterKeysToObj = (globalFilterApplied) => {
  const obj = {};
  globalFilterApplied.forEach((key) => {
    const splitKey = key.split('-|');
    // Backend requires snake case - but what was returned to me is not even a camel case,
    // is something like a typical string e.g. "Shift Type".
    const category = splitKey[1].replace(/ /g, '_').toLowerCase();
    const item = splitKey[2];

    if (!obj[category]) {
      obj[category] = [];
    }

    if (item) {
      obj[category].push(item);
    }
  });

  return obj;
};

export const FilterSection = ({
  filters, onChange, hookToState,
}) => filters ? (
  <>
    <Tree
      checkable
      checkedKeys={hookToState}
      selectable={false}
      defaultExpandAll
      onCheck={checkedKeys => onChange(checkedKeys)}
    >
      {
        filters.map(item => (
          <Tree.TreeNode
            title={item.label}
            key={item.id}
          >
            {
              item.innerList.map(innerItem => (
                <Tree.TreeNode
                  title={innerItem.label}
                  key={innerItem.id}
                />
              ))
            }
          </Tree.TreeNode>
        ))
      }
    </Tree>
  </>
) : '';

export const FilterSectionWithKpis = ({
  filters, kpiFilters,
  hookToState, kpiHookToState,
  onGlobalChange, onKpiChange,
}) => (
  <Row gutter={40}>
    <Col span={12}>
      <FilterSection
        filters={filters}
        hookToState={hookToState}
        onChange={onGlobalChange}
      />
    </Col>
    <Col span={12}>
      <FilterSection
        filters={kpiFilters}
        hookToState={kpiHookToState}
        onChange={onKpiChange}
      />
    </Col>
  </Row>
);

export const FilterBase = ({
  globalFilterTree, globalFilterHook, onGlobalChange, globalFilterLabel,
  kpiFilterTree, kpiFilterHook, onKpiChange, kpiFilterLabel, showFilterViewButton,
  onVisibleChange, pushHookToApplied, children, filters, hookToState,
  onClickToToggleFilterView, hideView, onChange, ...rest
}) => (
  <Popover
    overlayClassName="DCRPFilterPopover"
    onVisibleChange={hidden => onVisibleChange && onVisibleChange(hidden)}
    {...rest}
    title={kpiFilterTree ? (
      <FilterSectionWithKpis
        filters={globalFilterTree}
        kpiFilters={kpiFilterTree}
        hookToState={globalFilterHook}
        kpiHookToState={kpiFilterHook}
        onGlobalChange={onGlobalChange}
        onKpiChange={onKpiChange}
        globalFilterLabel={globalFilterLabel}
        kpiFilterLabel={kpiFilterLabel}
      />
    ) : (
      <FilterSection
        filters={globalFilterTree || filters}
        hookToState={globalFilterHook || hookToState}
        onChange={onGlobalChange || onChange}
      />
    )}
    content={(
      <>
        {
          pushHookToApplied && (
            <FilterPanelButton
              mr={3}
              type="primary"
              onClick={pushHookToApplied}
            >
              Apply filter
            </FilterPanelButton>
          )
        }

        {
          showFilterViewButton && (
            <Checkbox
              checked={!hideView}
              onChange={onClickToToggleFilterView}
            >
              Show applied filters view
            </Checkbox>
          )
        }
      </>
   )}
  >
    <PopoverButton
      mr={3}
      ml={2}
      {...rest}
    >
      {children}
    </PopoverButton>
  </Popover>
);

export class GeneralFilter extends Component {
  // TODO: Now KPI filters are treated to be a part of global filter. Need refactor.
  state = {
    showPopover: false,
    globalFilterTree: [],
    kpiFilterTree: [],
    globalFilterHook: [],
    kpiFilterHook: [],
  };

  componentDidMount() {
    this.makeTree();
    const { globalFilterApplied, kpiFilterApplied } = this.props;
    this.handleChangeToUpdateGlobalFilter(globalFilterApplied);
    this.handleChangeToUpdateKpiFilter(kpiFilterApplied);
  }

  componentDidUpdate(prevProps) {
    const { filterObj, globalFilterApplied, kpiFilterApplied } = this.props;

    if (prevProps.filterObj !== filterObj) {
      this.makeTree();
    }

    if (prevProps.globalFilterApplied !== globalFilterApplied) {
      this.handleChangeToUpdateGlobalFilter(globalFilterApplied);
    }

    if (prevProps.kpiFilterApplied !== kpiFilterApplied) {
      this.handleChangeToUpdateKpiFilter(kpiFilterApplied);
    }

    return true;
  }

  makeTree = () => {
    const { filterObj } = this.props;
    Object.keys(filterObj).forEach((key) => {
      const tree = generateFilterTreeObj(filterObj[key].list);
      this.setState({
        [`${key}Tree`]: tree,
      });
    });
  }

  pushHookToApplied = () => {
    this.setState({ showPopover: false });
    const { onClickToApplyFilter } = this.props;
    const { globalFilterHook, kpiFilterHook } = this.state;
    onClickToApplyFilter(globalFilterHook, kpiFilterHook);
  }

  // This function only updates the global filter panel. The actual filtering is
  // processed in CardSpacer.js for calling the function supplied by applyFilter.js
  handleChangeToUpdateGlobalFilter = (changedFilters) => {
    this.setState({
      globalFilterHook: changedFilters,
    });
  }

  // Same as above function but applies to card only filter.
  handleChangeToUpdateKpiFilter = (changedFilters) => {
    this.setState({
      kpiFilterHook: changedFilters,
    });
  }

  // If the filter panel closes but nothing saves, revert to previous state.
  handleFilterPanelVisibleChange = (hidden) => {
    if (hidden) {
      this.setState({ showPopover: true });
      return false;
    }

    const { globalFilterApplied, kpiFilterApplied } = this.props;
    this.setState({
      showPopover: false,
      globalFilterHook: globalFilterApplied,
      kpiFilterHook: kpiFilterApplied,
    });
    return true;
  }

  render() {
    const { globalFilterApplied, kpiFilterApplied, disabled } = this.props;
    const { showPopover } = this.state;
    return (
      <FilterBase
        {...this.props}
        {...this.state}
        visible={showPopover}
        onVisibleChange={hidden => this.handleFilterPanelVisibleChange(hidden)}
        onGlobalChange={this.handleChangeToUpdateGlobalFilter}
        onKpiChange={this.handleChangeToUpdateKpiFilter}
        pushHookToApplied={this.pushHookToApplied}
        placement="bottomRight"
        trigger="click"
        color={disabled ? 'greyscale.3' : 'greyscale.1'}
        mr={0}
        ml="-14px"
        fontSize="18px"
        showFilterViewButton
      >
        <Icon
          type="filter"
          theme={
            ([...globalFilterApplied, ...kpiFilterApplied].length > 0)
              ? 'filled' : 'outlined'
          }
        />
      </FilterBase>
    );
  }
}

export const PanelFilter = (props) => {
  const { hookToState } = props;
  return (
    <FilterBase
      {...props}
      placement="topLeft"
    >
      <Icon
        type="filter"
        theme={
          (hookToState && hookToState.length > 0)
            ? 'filled' : 'outlined'
        }
      />
    </FilterBase>
  );
};
